import { useCallback, useEffect, useMemo, useRef, useState, type DragEvent, type ReactNode } from 'react';
import { Link, NavLink, useLocation, useNavigate, useParams, useSearchParams } from 'react-router';
import { Icon, type IconName } from '../components/Icon.tsx';
import { beforeFor } from '../components/Reorder.tsx';
import { Shell } from '../components/Shell.tsx';
import { Button, Confirm, Dialog, EmptyState, IconButton, Skeleton, StatusBadge } from '../components/ui.tsx';
import { currentLang, t, tn, type MessageKey } from '../i18n/index.ts';
import { action, ApiError, subscribe } from '../lib/api.ts';
import { useUi } from '../lib/ui-state.tsx';
import { Editor, newId, toMarkdown, type Block, type EditorHandle, type PageRef } from './knowledge/Editor.tsx';
import { Inline } from './knowledge/inline.tsx';

type Section = 'workspace' | 'shared' | 'private';
interface TreePage extends PageRef {
  parent: string | null;
  sort: number;
  section: Section;
  can_write: boolean;
  edited_at: string;
}
interface Home {
  me: string;
  manager: boolean;
  pages: TreePage[];
  favorites: string[];
  recent: { id: string; at: string }[];
  trash: number;
  people: { id: string; name: string }[];
}
type Access = 'read' | 'write';
interface Page {
  id: string;
  title: string;
  icon: string | null;
  body: Block[];
  version: number;
  full_width: boolean;
  parent: string | null;
  path: PageRef[];
  children: PageRef[];
  edited_by: string | null;
  edited_at: string;
  created_at: string;
  owner: string;
  trashed_at: string | null;
  trashed_by: string | null;
  favorite: boolean;
  can_write: boolean;
  can_share: boolean;
  section: Section;
  revisions: number;
  root: {
    id: string;
    title: string;
    icon: string | null;
    visibility: 'workspace' | 'members' | 'private';
    everyone_can: Access;
    owner: string;
    owner_name: string;
    members: { user: string; name: string; access: Access }[];
  };
}

const SECTIONS: Section[] = ['workspace', 'shared', 'private'];
const SECTION_ICON: Record<Section, IconName> = { workspace: 'layers', shared: 'users', private: 'lock' };
const EMOJIS = [
  '📄',
  '📝',
  '📌',
  '📎',
  '📚',
  '📖',
  '🗂️',
  '🗓️',
  '✅',
  '☑️',
  '⚠️',
  '❗',
  '💡',
  '🧭',
  '🚀',
  '🎯',
  '👋',
  '🤝',
  '👥',
  '🔒',
  '🔑',
  '🏠',
  '🏢',
  '🏪',
  '🍽️',
  '🍳',
  '🍰',
  '🥗',
  '🍷',
  '☕',
  '🧾',
  '💶',
  '📦',
  '🚚',
  '🛠️',
  '🔧',
  '🧹',
  '🧯',
  '🩺',
  '📞',
  '✉️',
  '💬',
  '📣',
  '📊',
  '📈',
  '🗺️',
  '🌅',
  '🌙',
];
const untitled = (s: string | null | undefined) => s || t('kb.untitled');
const ago = (iso: string) => {
  const s = Math.round((Date.now() - Date.parse(iso)) / 1000);
  const rtf = new Intl.RelativeTimeFormat(currentLang(), { numeric: 'auto' });
  if (s < 45) return t('kb.just_now');
  if (s < 3600) return rtf.format(-Math.round(s / 60), 'minute');
  if (s < 86_400) return rtf.format(-Math.round(s / 3600), 'hour');
  if (s < 30 * 86_400) return rtf.format(-Math.round(s / 86_400), 'day');
  return new Intl.DateTimeFormat(currentLang(), { day: 'numeric', month: 'short', year: 'numeric' }).format(new Date(iso));
};
const fullDate = (iso: string) => new Intl.DateTimeFormat(currentLang(), { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(iso));

const usePhone = () => {
  const q = '(max-width: 760px)';
  const [phone, setPhone] = useState(() => window.matchMedia(q).matches);
  useEffect(() => {
    const m = window.matchMedia(q);
    const on = () => setPhone(m.matches);
    m.addEventListener('change', on);
    return () => m.removeEventListener('change', on);
  }, []);
  return phone;
};

const readOpen = (): Record<string, boolean> => {
  try {
    return JSON.parse(localStorage.getItem('erp.kb.open') ?? '{}');
  } catch {
    return {};
  }
};

/** Ready pages to start from, in the person's language. */
function template(key: string): { title: string; icon: string; body: Block[] } | null {
  const b = (type: Block['type'], k: MessageKey | '', extra: Partial<Block> = {}): Block => ({ id: newId(), type, text: k ? t(k) : '', ...extra });
  switch (key) {
    case 'meeting':
      return {
        title: t('kb.tpl_meeting'),
        icon: '🗓️',
        body: [
          b('callout', 'kb.tpl_meeting_who', { tone: 'info' }),
          b('h2', 'kb.tpl_agenda'),
          b('number', 'kb.tpl_topic'),
          b('number', 'kb.tpl_topic'),
          b('h2', 'kb.tpl_notes'),
          b('p', ''),
          b('h2', 'kb.tpl_decisions'),
          b('bullet', ''),
          b('h2', 'kb.tpl_next'),
          b('todo', 'kb.tpl_next_todo', { checked: false }),
        ],
      };
    case 'howto':
      return {
        title: t('kb.tpl_howto'),
        icon: '🧭',
        body: [
          b('p', 'kb.tpl_howto_for'),
          b('h2', 'kb.tpl_before'),
          b('bullet', 'kb.tpl_need'),
          b('h2', 'kb.tpl_steps'),
          b('number', 'kb.tpl_step'),
          b('number', 'kb.tpl_step'),
          b('number', 'kb.tpl_step'),
          b('callout', 'kb.tpl_wrong', { tone: 'warning' }),
        ],
      };
    case 'checklist':
      return {
        title: t('kb.tpl_checklist'),
        icon: '✅',
        body: [b('p', 'kb.tpl_checklist_when'), b('todo', 'kb.tpl_check', { checked: false }), b('todo', 'kb.tpl_check', { checked: false }), b('todo', 'kb.tpl_check', { checked: false })],
      };
    case 'faq':
      return { title: t('kb.tpl_faq'), icon: '💬', body: [b('h3', 'kb.tpl_question'), b('p', 'kb.tpl_answer'), b('h3', 'kb.tpl_question'), b('p', 'kb.tpl_answer')] };
    case 'project':
      return {
        title: t('kb.tpl_project'),
        icon: '🚀',
        body: [
          b('h2', 'kb.tpl_why'),
          b('p', 'kb.tpl_why_text'),
          b('h2', 'kb.tpl_what'),
          b('bullet', 'kb.tpl_in'),
          b('bullet', 'kb.tpl_out'),
          b('h2', 'kb.tpl_who'),
          b('bullet', 'kb.tpl_who_text'),
          b('h2', 'kb.tpl_when'),
          b('todo', 'kb.tpl_milestone', { checked: false }),
          b('divider', ''),
          b('callout', 'kb.tpl_open', { tone: 'warning' }),
        ],
      };
  }
  return null;
}
const TEMPLATES: { key: string; icon: string; label: MessageKey; hint: MessageKey }[] = [
  { key: 'meeting', icon: '🗓️', label: 'kb.tpl_meeting', hint: 'kb.tpl_meeting_hint' },
  { key: 'howto', icon: '🧭', label: 'kb.tpl_howto', hint: 'kb.tpl_howto_hint' },
  { key: 'checklist', icon: '✅', label: 'kb.tpl_checklist', hint: 'kb.tpl_checklist_hint' },
  { key: 'faq', icon: '💬', label: 'kb.tpl_faq', hint: 'kb.tpl_faq_hint' },
  { key: 'project', icon: '🚀', label: 'kb.tpl_project', hint: 'kb.tpl_project_hint' },
];

// ---- the screen ---------------------------------------------------------------------------------

export function Knowledge() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const phone = usePhone();
  const { fail } = useUi();
  const [home, setHome] = useState<Home | null>(null);
  const [open, setOpen] = useState<Record<string, boolean>>(readOpen);
  const [drawer, setDrawer] = useState(false);
  const [searching, setSearching] = useState(false);
  const view = params.get('view');

  const load = useCallback(() => {
    action<Home>('knowledge.home').then(setHome, fail);
  }, [fail]);
  useEffect(() => {
    load();
    let timer: ReturnType<typeof setTimeout> | null = null;
    const stop = subscribe(['knowledge'], (e) => {
      if (e === 'ready') return;
      if (timer) clearTimeout(timer);
      timer = setTimeout(load, 400);
    });
    return () => {
      stop();
      if (timer) clearTimeout(timer);
    };
  }, [load]);
  useEffect(() => {
    try {
      localStorage.setItem('erp.kb.open', JSON.stringify(open));
    } catch {
      /* private mode */
    }
  }, [open]);
  // The pages above the open one are unfolded, so it can be seen in the tree.
  useEffect(() => {
    if (!home || !id) return;
    const byId = new Map(home.pages.map((p) => [p.id, p]));
    const add: Record<string, boolean> = {};
    for (let p = byId.get(id)?.parent; p; p = byId.get(p)?.parent) if (!open[p]) add[p] = true;
    if (Object.keys(add).length) setOpen((o) => ({ ...o, ...add }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [home, id]);
  useEffect(() => setDrawer(false), [id, view]);
  useEffect(() => {
    const key = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k' && !(e.target as HTMLElement).closest?.('.kb-editor')) {
        e.preventDefault();
        setSearching(true);
      }
    };
    window.addEventListener('keydown', key);
    return () => window.removeEventListener('keydown', key);
  }, []);

  const create = async (parent: string | null, section: 'workspace' | 'private' = 'workspace', tpl?: string) => {
    try {
      const tp = tpl ? template(tpl) : null;
      const r = await action<{ id: string }>('knowledge.create', { parent, section, ...(tp ? { title: tp.title, icon: tp.icon, body: tp.body } : {}) });
      if (parent) setOpen((o) => ({ ...o, [parent]: true }));
      load();
      navigate(`/knowledge/${r.id}`, { state: { fresh: !tp } });
      return r.id;
    } catch (e) {
      fail(e);
      return null;
    }
  };

  const tree = home ? <Tree home={home} current={id ?? null} open={open} setOpen={setOpen} onCreate={create} onSearch={() => setSearching(true)} onMoved={load} trashOn={view === 'trash'} /> : null;
  return (
    <Shell>
      <div className="page kb-page">
        <div className="kb">
          {phone ? null : <aside className="panel kb-side">{tree ?? <Skeleton h={400} />}</aside>}
          <section className="panel kb-main">
            {!home ? (
              <div className="kb-pad">
                <Skeleton h={36} w="60%" />
                <Skeleton h={240} />
              </div>
            ) : view === 'trash' ? (
              <TrashView home={home} onChanged={load} onMenu={phone ? () => setDrawer(true) : undefined} />
            ) : id ? (
              <ArticleView key={id} id={id} home={home} onTree={load} onCreate={create} onMenu={phone ? () => setDrawer(true) : undefined} />
            ) : (
              <HomeView home={home} onCreate={create} onSearch={() => setSearching(true)} onMenu={phone ? () => setDrawer(true) : undefined} />
            )}
          </section>
        </div>
      </div>
      {phone && drawer ? (
        <div className="overlay kb-drawer-back" onMouseDown={(e) => e.target === e.currentTarget && setDrawer(false)}>
          <div className="kb-drawer" role="dialog" aria-modal="true" aria-label={t('kb.pages')}>
            <header className="dialog-head">
              <h2>{t('kb.pages')}</h2>
              <IconButton icon="x" label={t('common.close')} onClick={() => setDrawer(false)} />
            </header>
            {tree}
          </div>
        </div>
      ) : null}
      {searching && home ? <SearchDialog home={home} onClose={() => setSearching(false)} /> : null}
    </Shell>
  );
}

// ---- the tree of pages --------------------------------------------------------------------------

function Tree({
  home,
  current,
  open,
  setOpen,
  onCreate,
  onSearch,
  onMoved,
  trashOn,
}: {
  home: Home;
  current: string | null;
  open: Record<string, boolean>;
  setOpen: (f: (o: Record<string, boolean>) => Record<string, boolean>) => void;
  onCreate: (parent: string | null, section?: 'workspace' | 'private') => void;
  onSearch: () => void;
  onMoved: () => void;
  trashOn: boolean;
}) {
  const { fail, toast } = useUi();
  const [drag, setDrag] = useState<{ id: string; over: string | null; zone: 'before' | 'inside' | 'after' | 'section' } | null>(null);
  const kids = useMemo(() => {
    const m = new Map<string | null, TreePage[]>();
    for (const p of home.pages) {
      const k = p.parent;
      if (!m.has(k)) m.set(k, []);
      m.get(k)!.push(p);
    }
    for (const list of m.values()) list.sort((a, b) => a.sort - b.sort);
    return m;
  }, [home.pages]);
  const byId = useMemo(() => new Map(home.pages.map((p) => [p.id, p])), [home.pages]);
  const isInside = (id: string, maybeParent: string) => {
    for (let p: string | null | undefined = id; p; p = byId.get(p)?.parent) if (p === maybeParent) return true;
    return false;
  };

  const drop = async (target: TreePage | null, zone: 'before' | 'inside' | 'after' | 'section', section?: Section) => {
    const d = drag;
    setDrag(null);
    if (!d) return;
    const moving = byId.get(d.id);
    if (!moving) return;
    let input: Record<string, unknown>;
    if (zone === 'section' || !target) {
      if (section === 'shared') return;
      input = { id: d.id, parent: null, section, before: kids.get(null)?.find((p) => p.section === section && p.id !== d.id)?.id ?? null };
    } else if (zone === 'inside') {
      if (isInside(target.id, d.id)) return;
      input = { id: d.id, parent: target.id };
    } else {
      if (isInside(target.id, d.id) && target.id !== d.id) return;
      if (target.id === d.id) return;
      const siblings = (kids.get(target.parent) ?? []).filter((p) => p.section === target.section && p.id !== d.id);
      const at = siblings.findIndex((p) => p.id === target.id);
      const before = zone === 'before' ? target.id : (siblings[at + 1]?.id ?? null);
      input = { id: d.id, parent: target.parent, before, ...(target.parent ? {} : { section: target.section === 'private' ? 'private' : 'workspace' }) };
      if (!target.parent && target.section === 'shared') input.section = null;
    }
    try {
      await action('knowledge.move', input);
      if (zone === 'inside' && target) setOpen((o) => ({ ...o, [target.id]: true }));
      onMoved();
      toast(t('kb.moved'));
    } catch (e) {
      fail(e);
    }
  };

  const zoneOf = (e: DragEvent, el: HTMLElement): 'before' | 'inside' | 'after' => {
    const r = el.getBoundingClientRect();
    const y = (e.clientY - r.top) / r.height;
    return y < 0.28 ? 'before' : y > 0.72 ? 'after' : 'inside';
  };

  const node = (p: TreePage, depth: number): ReactNode => {
    const children = kids.get(p.id) ?? [];
    const isOpen = !!open[p.id];
    const over = drag?.over === p.id ? drag.zone : null;
    return (
      <li key={p.id} role="treeitem" aria-expanded={children.length ? isOpen : undefined} aria-selected={current === p.id}>
        <div
          className={`kb-node${current === p.id ? ' on' : ''}${over ? ` drop-${over}` : ''}${drag?.id === p.id ? ' dragging' : ''}`}
          style={{ paddingLeft: 6 + depth * 14 }}
          draggable={p.can_write}
          onDragStart={(e) => {
            e.dataTransfer.effectAllowed = 'move';
            e.dataTransfer.setData('text/plain', p.id);
            setDrag({ id: p.id, over: null, zone: 'inside' });
          }}
          onDragEnd={() => setDrag(null)}
          onDragOver={(e) => {
            if (!drag || drag.id === p.id || isInside(p.id, drag.id)) return;
            e.preventDefault();
            const zone = zoneOf(e, e.currentTarget);
            if (drag.over !== p.id || drag.zone !== zone) setDrag({ ...drag, over: p.id, zone });
          }}
          onDrop={(e) => {
            e.preventDefault();
            void drop(p, drag?.zone ?? 'inside');
          }}
        >
          {children.length ? (
            <button type="button" className={`kb-twist${isOpen ? ' open' : ''}`} onClick={() => setOpen((o) => ({ ...o, [p.id]: !isOpen }))} aria-label={isOpen ? t('kb.fold') : t('kb.unfold')}>
              <Icon name="chevronRight" size={14} />
            </button>
          ) : (
            <span className="kb-twist none" aria-hidden="true" />
          )}
          <NavLink to={`/knowledge/${p.id}`} className="kb-node-link" draggable={false}>
            <span className="kb-emoji" aria-hidden="true">
              {p.icon || <Icon name="text" size={15} />}
            </span>
            <span className="kb-node-title">{untitled(p.title)}</span>
          </NavLink>
          {p.can_write ? (
            <button type="button" className="kb-node-add" onClick={() => onCreate(p.id)} aria-label={t('kb.add_subpage_in', { title: untitled(p.title) })} title={t('kb.add_subpage')}>
              <Icon name="plus" size={14} />
            </button>
          ) : null}
        </div>
        {children.length && isOpen ? (
          <ul role="group" className="kb-tree">
            {children.map((c) => node(c, depth + 1))}
          </ul>
        ) : null}
      </li>
    );
  };

  const favs = home.favorites.map((f) => byId.get(f)).filter((x): x is TreePage => !!x);
  return (
    <div className="kb-side-in">
      <div className="kb-side-top">
        <button type="button" className="kb-search-btn" onClick={onSearch}>
          <Icon name="search" size={16} />
          <span>{t('kb.search')}</span>
          <kbd>{/Mac/.test(navigator.platform) ? '⌘K' : 'Ctrl K'}</kbd>
        </button>
        <Button variant="primary" icon="plus" onClick={() => onCreate(null, 'workspace')} className="kb-new">
          {t('kb.new_page')}
        </Button>
      </div>
      <nav className="kb-sections" aria-label={t('kb.pages')}>
        {favs.length ? (
          <div className="kb-sec">
            <h3 className="kb-sec-head">
              <Icon name="star" size={14} />
              {t('kb.sec_favorites')}
            </h3>
            <ul className="kb-tree" role="tree" aria-label={t('kb.sec_favorites')}>
              {favs.map((p) => (
                <li key={p.id} role="treeitem" aria-selected={current === p.id}>
                  <div className={`kb-node${current === p.id ? ' on' : ''}`} style={{ paddingLeft: 6 }}>
                    <span className="kb-twist none" aria-hidden="true" />
                    <NavLink to={`/knowledge/${p.id}`} className="kb-node-link">
                      <span className="kb-emoji" aria-hidden="true">
                        {p.icon || <Icon name="text" size={15} />}
                      </span>
                      <span className="kb-node-title">{untitled(p.title)}</span>
                    </NavLink>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
        {SECTIONS.map((sec) => {
          const tops = (kids.get(null) ?? []).filter((p) => p.section === sec);
          if (sec === 'shared' && !tops.length) return null;
          const over = drag?.over === `sec:${sec}`;
          return (
            <div className="kb-sec" key={sec}>
              <h3
                className={`kb-sec-head${over ? ' drop-inside' : ''}`}
                onDragOver={(e) => {
                  if (!drag || sec === 'shared') return;
                  e.preventDefault();
                  if (!over) setDrag({ ...drag, over: `sec:${sec}`, zone: 'section' });
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  void drop(null, 'section', sec);
                }}
              >
                <Icon name={SECTION_ICON[sec]} size={14} />
                {t(`kb.sec_${sec}` as MessageKey)}
                {sec !== 'shared' ? (
                  <button
                    type="button"
                    className="kb-sec-add"
                    onClick={() => onCreate(null, sec)}
                    aria-label={t(sec === 'private' ? 'kb.new_private' : 'kb.new_page')}
                    title={t(sec === 'private' ? 'kb.new_private' : 'kb.new_page')}
                  >
                    <Icon name="plus" size={14} />
                  </button>
                ) : null}
              </h3>
              {tops.length ? (
                <ul className="kb-tree" role="tree" aria-label={t(`kb.sec_${sec}` as MessageKey)}>
                  {tops.map((p) => node(p, 0))}
                </ul>
              ) : (
                <p className="kb-sec-empty muted small">{t(sec === 'private' ? 'kb.private_empty' : 'kb.workspace_empty')}</p>
              )}
            </div>
          );
        })}
      </nav>
      <Link to="/knowledge?view=trash" className={`kb-trash-link${trashOn ? ' on' : ''}`}>
        <Icon name="trash" size={16} />
        {t('kb.trash')}
        {home.trash ? <span className="kb-count">{home.trash}</span> : null}
      </Link>
    </div>
  );
}

// ---- one page -----------------------------------------------------------------------------------

type SaveState = 'idle' | 'dirty' | 'saving' | 'saved' | 'error';

function ArticleView({
  id,
  home,
  onTree,
  onCreate,
  onMenu,
}: {
  id: string;
  home: Home;
  onTree: () => void;
  onCreate: (parent: string | null, section?: 'workspace' | 'private', tpl?: string) => Promise<string | null>;
  onMenu?: () => void;
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { fail, toast } = useUi();
  const [page, setPage] = useState<Page | null>(null);
  const [missing, setMissing] = useState(false);
  const [title, setTitle] = useState('');
  const [icon, setIcon] = useState<string | null>(null);
  const [blocks, setBlocks] = useState<Block[]>([]);
  const [state, setState] = useState<SaveState>('idle');
  const [conflict, setConflict] = useState<{ by: string | null; version: number } | null>(null);
  const [outside, setOutside] = useState<string | null>(null);
  const [dialog, setDialog] = useState<null | 'share' | 'move' | 'history' | 'trash' | 'icon'>(null);
  const [more, setMore] = useState(false);
  const version = useRef(0);
  const dirty = useRef(false);
  const latest = useRef({ title, icon, blocks });
  latest.current = { title, icon, blocks };
  const editor = useRef<EditorHandle>(null);
  const titleRef = useRef<HTMLTextAreaElement>(null);
  const saving = useRef<Promise<void> | null>(null);

  const apply = (p: Page) => {
    setPage(p);
    setTitle(p.title);
    setIcon(p.icon);
    setBlocks(p.body);
    version.current = p.version;
    dirty.current = false;
    setState('idle');
    setConflict(null);
    setOutside(null);
  };
  const reload = useCallback(
    (visit = false) =>
      action<Page>('knowledge.get', { id, visit }).then(apply, (e) => {
        if (e instanceof ApiError && e.status === 404) setMissing(true);
        else fail(e);
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [id],
  );
  useEffect(() => {
    void reload(true);
  }, [reload]);

  // A new page starts with the cursor in its title.
  useEffect(() => {
    if (page && (location.state as { fresh?: boolean } | null)?.fresh && !page.title) titleRef.current?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page?.id]);

  const save = useCallback(
    async (force?: number) => {
      if (!dirty.current && force === undefined) return;
      if (saving.current) await saving.current;
      const { title: ti, icon: ic, blocks: bl } = latest.current;
      dirty.current = false;
      setState('saving');
      const run = action<{ version: number }>('knowledge.save', { id, version: force ?? version.current, title: ti, icon: ic, body: bl })
        .then((r) => {
          version.current = r.version;
          setConflict(null);
          setState(dirty.current ? 'dirty' : 'saved');
          if (ti !== page?.title || ic !== page?.icon) onTree();
        })
        .catch((e) => {
          dirty.current = true;
          if (e instanceof ApiError && e.code === 'conflict' && e.details?.version) {
            setConflict({ by: (e.details.by as string) ?? null, version: e.details.version as number });
            setState('error');
          } else {
            setState('error');
            fail(e);
          }
        })
        .finally(() => {
          saving.current = null;
        });
      saving.current = run;
      await run;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [id, page?.title, page?.icon],
  );

  // Saves a moment after the last change, and when leaving the page.
  useEffect(() => {
    if (!dirty.current || conflict) return;
    const timer = setTimeout(() => void save(), 900);
    return () => clearTimeout(timer);
  }, [title, icon, blocks, save, conflict]);
  useEffect(() => {
    const warn = (e: BeforeUnloadEvent) => {
      if (dirty.current) e.preventDefault();
    };
    window.addEventListener('beforeunload', warn);
    return () => {
      window.removeEventListener('beforeunload', warn);
      if (dirty.current) void save();
    };
  }, [save]);

  // Someone else saved: show it at once when nothing is being written here, otherwise say so.
  useEffect(
    () =>
      subscribe(['knowledge.changed'], (e, data: { ids?: string[]; version?: number | null; by?: string | null }) => {
        if (e === 'ready' || !data?.ids?.includes(id)) return;
        if (data.version && data.version <= version.current) return;
        if (data.by === home.me && !data.version) return void reload();
        if (data.by === home.me) return;
        if (dirty.current) setOutside(home.people.find((p) => p.id === data.by)?.name ?? '');
        else void reload();
      }),
    [id, home.me, home.people, reload],
  );

  const change =
    <T,>(setter: (v: T) => void) =>
    (v: T) => {
      dirty.current = true;
      setState('dirty');
      setter(v);
    };
  const setBlocksD = change(setBlocks);
  const setTitleD = change(setTitle);
  const setIconD = change(setIcon);

  if (missing)
    return (
      <div className="kb-pad">
        <EmptyState
          icon="notebook"
          title={t('kb.not_found')}
          body={t('kb.not_found_hint')}
          action={
            <Button variant="primary" onClick={() => navigate('/knowledge')}>
              {t('kb.back_home')}
            </Button>
          }
        />
      </div>
    );
  if (!page)
    return (
      <div className="kb-pad">
        <Skeleton h={20} w="40%" />
        <Skeleton h={44} w="70%" />
        <Skeleton h={300} />
      </div>
    );

  const ro = !page.can_write;
  const pages: PageRef[] = home.pages.filter((p) => p.id !== page.id);
  const favourite = async () => {
    try {
      await action('knowledge.favorite', { id, on: !page.favorite });
      setPage({ ...page, favorite: !page.favorite });
      onTree();
    } catch (e) {
      fail(e);
    }
  };
  const duplicate = async () => {
    setMore(false);
    await save();
    try {
      const r = await action<{ id: string }>('knowledge.duplicate', { id });
      onTree();
      navigate(`/knowledge/${r.id}`);
      toast(t('kb.duplicated'));
    } catch (e) {
      fail(e);
    }
  };
  const download = () => {
    setMore(false);
    const blob = new Blob([toMarkdown(title, blocks)], { type: 'text/markdown;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `${(title || t('kb.untitled')).replace(/[\\/:*?"<>|]+/g, ' ').trim()}.md`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 1000);
  };
  const toggleWidth = async () => {
    setMore(false);
    try {
      await save();
      const r = await action<{ version: number }>('knowledge.save', { id, version: version.current, full_width: !page.full_width });
      version.current = r.version;
      setPage({ ...page, full_width: !page.full_width });
    } catch (e) {
      fail(e);
    }
  };

  const status =
    state === 'saving' ? (
      <span className="kb-status">
        <span className="spinner" aria-hidden="true" /> {t('kb.saving')}
      </span>
    ) : state === 'dirty' ? (
      <span className="kb-status">{t('kb.unsaved')}</span>
    ) : state === 'saved' ? (
      <span className="kb-status ok">
        <Icon name="check" size={14} /> {t('kb.saved')}
      </span>
    ) : state === 'error' && !conflict ? (
      <button type="button" className="kb-status bad link-button" onClick={() => void save(version.current)}>
        <Icon name="alert" size={14} /> {t('kb.save_failed')}
      </button>
    ) : null;

  const sectionLabel = t(`kb.sec_${page.section}` as MessageKey);
  return (
    <article className={`kb-article${page.full_width ? ' wide' : ''}`}>
      <header className="kb-top">
        {onMenu ? <IconButton icon="menu" label={t('kb.pages')} onClick={onMenu} /> : null}
        <nav className="kb-crumbs" aria-label={t('kb.where')}>
          <span className="kb-crumb-sec">
            <Icon name={SECTION_ICON[page.section]} size={14} />
            {sectionLabel}
          </span>
          {page.path.map((p) => (
            <span key={p.id} className="kb-crumb">
              <Icon name="chevronRight" size={12} />
              <Link to={`/knowledge/${p.id}`}>
                {p.icon ? <span aria-hidden="true">{p.icon} </span> : null}
                {untitled(p.title)}
              </Link>
            </span>
          ))}
        </nav>
        <div className="kb-actions">
          <span aria-live="polite">{status}</span>
          {ro && !page.trashed_at ? <StatusBadge tone="neutral" icon="eye" label={t('kb.read_only')} /> : null}
          {!page.trashed_at ? (
            <IconButton
              icon="star"
              label={page.favorite ? t('kb.unfavorite') : t('kb.favorite')}
              onClick={() => void favourite()}
              className={page.favorite ? 'kb-fav on' : 'kb-fav'}
              aria-pressed={page.favorite}
            />
          ) : null}
          {!page.trashed_at ? (
            <Button size="sm" icon={page.root.visibility === 'private' ? 'lock' : page.root.visibility === 'members' ? 'users' : 'share'} onClick={() => setDialog('share')}>
              {t('kb.share')}
            </Button>
          ) : null}
          {!page.trashed_at ? (
            <div className="kb-more">
              <IconButton icon="dots" label={t('kb.more')} onClick={() => setMore(!more)} aria-expanded={more} />
              {more ? (
                <Popover onClose={() => setMore(false)}>
                  {page.can_write ? (
                    <MenuItem
                      icon="plus"
                      label={t('kb.add_subpage')}
                      onClick={() => {
                        setMore(false);
                        void onCreate(page.id);
                      }}
                    />
                  ) : null}
                  <MenuItem icon="copy" label={t('kb.duplicate')} onClick={() => void duplicate()} />
                  {page.can_write ? (
                    <MenuItem
                      icon="move"
                      label={t('kb.move_to')}
                      onClick={() => {
                        setMore(false);
                        setDialog('move');
                      }}
                    />
                  ) : null}
                  {page.can_write
                    ? (() => {
                        // Its place among the pages next to it, changed without dragging the tree (WCAG 2.5.7).
                        const me = home.pages.find((p) => p.id === page.id);
                        if (!me || (!me.parent && me.section === 'shared')) return null;
                        const row = home.pages
                          .filter((p) => p.parent === me.parent && (me.parent || p.section === me.section))
                          .sort((a, b) => a.sort - b.sort)
                          .map((p) => p.id);
                        const i = row.indexOf(page.id);
                        const shift = async (by: -1 | 1) => {
                          setMore(false);
                          try {
                            await action('knowledge.move', { id: page.id, parent: me.parent, before: beforeFor(row, i, by), ...(me.parent ? {} : { section: me.section }) });
                            await reload();
                            onTree();
                          } catch (e) {
                            fail(e);
                          }
                        };
                        return (
                          <>
                            {i > 0 ? <MenuItem icon="arrowUp" label={t('common.move_up')} onClick={() => void shift(-1)} /> : null}
                            {i >= 0 && i < row.length - 1 ? <MenuItem icon="arrowDown" label={t('common.move_down')} onClick={() => void shift(1)} /> : null}
                          </>
                        );
                      })()
                    : null}
                  <MenuItem
                    icon="history"
                    label={t('kb.history')}
                    hint={page.revisions ? String(page.revisions) : undefined}
                    onClick={() => {
                      setMore(false);
                      void save().then(() => setDialog('history'));
                    }}
                  />
                  {page.can_write ? <MenuItem icon="move" label={page.full_width ? t('kb.normal_width') : t('kb.full_width')} onClick={() => void toggleWidth()} /> : null}
                  <MenuItem icon="download" label={t('kb.download_md')} onClick={download} />
                  <MenuItem
                    icon="printer"
                    label={t('kb.print')}
                    onClick={() => {
                      setMore(false);
                      setTimeout(() => window.print(), 50);
                    }}
                  />
                  {page.can_write ? (
                    <MenuItem
                      icon="trash"
                      danger
                      label={t('kb.to_trash')}
                      onClick={() => {
                        setMore(false);
                        setDialog('trash');
                      }}
                    />
                  ) : null}
                </Popover>
              ) : null}
            </div>
          ) : null}
        </div>
      </header>

      {page.trashed_at ? (
        <TrashedBanner
          page={page}
          home={home}
          onDone={() => {
            onTree();
            void reload();
          }}
        />
      ) : null}
      {conflict ? (
        <div className="kb-banner warn" role="alert">
          <Icon name="alert" />
          <p>{conflict.by ? t('kb.conflict', { name: conflict.by }) : t('kb.conflict_someone')}</p>
          <Button size="sm" onClick={() => void reload()}>
            {t('kb.conflict_theirs')}
          </Button>
          <Button size="sm" variant="primary" onClick={() => void save(conflict.version)}>
            {t('kb.conflict_mine')}
          </Button>
        </div>
      ) : outside !== null ? (
        <div className="kb-banner info" role="status">
          <Icon name="info" />
          <p>{outside ? t('kb.changed_by', { name: outside }) : t('kb.changed_someone')}</p>
        </div>
      ) : null}
      {ro && !page.trashed_at ? (
        <div className="kb-banner info">
          <Icon name="eye" />
          <p>{t('kb.read_only_hint', { name: page.root.owner_name })}</p>
        </div>
      ) : null}

      <div className="kb-doc">
        <div className="kb-head">
          {ro ? (
            icon ? (
              <span className="kb-big-emoji" aria-hidden="true">
                {icon}
              </span>
            ) : null
          ) : (
            <button type="button" className={`kb-icon-btn${icon ? '' : ' empty'}`} onClick={() => setDialog('icon')} aria-label={icon ? t('kb.change_icon') : t('kb.add_icon')}>
              {icon ? (
                <span className="kb-big-emoji">{icon}</span>
              ) : (
                <>
                  <Icon name="plus" size={16} /> {t('kb.add_icon')}
                </>
              )}
            </button>
          )}
          {ro ? (
            <h1 className="kb-title">{untitled(title)}</h1>
          ) : (
            <TitleInput inputRef={titleRef} value={title} onChange={setTitleD} onEnter={() => editor.current?.focusStart()} onDown={() => editor.current?.focusStart()} />
          )}
          <p className="kb-meta muted small">
            {page.edited_by ? t('kb.edited_by', { name: page.edited_by, when: ago(page.edited_at) }) : t('kb.edited', { when: ago(page.edited_at) })}
            <span aria-hidden="true"> · </span>
            {page.root.visibility === 'workspace'
              ? page.root.everyone_can === 'write'
                ? t('kb.vis_workspace_write')
                : t('kb.vis_workspace_read')
              : page.root.visibility === 'members'
                ? tn('kb.vis_members', page.root.members.length + 1)
                : t('kb.vis_private')}
          </p>
        </div>
        <Editor
          ref={editor}
          pageId={page.id}
          blocks={blocks}
          onChange={setBlocksD}
          readOnly={ro}
          pages={pages}
          onTitleFocus={() => {
            const el = titleRef.current;
            if (el) {
              el.focus();
              el.setSelectionRange(el.value.length, el.value.length);
            }
          }}
          onSubpage={async () => {
            await save();
            try {
              const r = await action<{ id: string }>('knowledge.create', { parent: page.id, title: t('kb.new_subpage_title') });
              onTree();
              return { id: r.id, title: t('kb.new_subpage_title'), icon: null };
            } catch (e) {
              fail(e);
              return null;
            }
          }}
        />
        {page.children.length || page.can_write ? (
          <section className="kb-children" aria-label={t('kb.subpages')}>
            <h2 className="kb-children-head">{t('kb.subpages')}</h2>
            <div className="kb-cards">
              {page.children.map((c) => (
                <Link key={c.id} to={`/knowledge/${c.id}`} className="kb-card">
                  <span className="kb-emoji" aria-hidden="true">
                    {c.icon || <Icon name="text" size={16} />}
                  </span>
                  <span>{untitled(c.title)}</span>
                </Link>
              ))}
              {page.can_write ? (
                <button type="button" className="kb-card add" onClick={() => void save().then(() => onCreate(page.id))}>
                  <Icon name="plus" size={16} />
                  <span>{t('kb.add_subpage')}</span>
                </button>
              ) : null}
            </div>
          </section>
        ) : null}
      </div>

      {dialog === 'icon' ? (
        <EmojiDialog
          value={icon}
          onClose={() => setDialog(null)}
          onPick={(e) => {
            setIconD(e);
            setDialog(null);
          }}
        />
      ) : null}
      {dialog === 'share' ? (
        <ShareDialog
          page={page}
          home={home}
          onClose={() => setDialog(null)}
          onSaved={() => {
            setDialog(null);
            void reload();
            onTree();
          }}
        />
      ) : null}
      {dialog === 'move' ? (
        <MoveDialog
          page={page}
          home={home}
          onClose={() => setDialog(null)}
          onMoved={() => {
            setDialog(null);
            void reload();
            onTree();
          }}
        />
      ) : null}
      {dialog === 'history' ? (
        <HistoryDialog
          page={page}
          onClose={() => setDialog(null)}
          onRestored={() => {
            setDialog(null);
            void reload();
            onTree();
            toast(t('kb.restored_version'));
          }}
        />
      ) : null}
      {dialog === 'trash' ? (
        <TrashConfirm
          page={page}
          home={home}
          onClose={() => setDialog(null)}
          onDone={() => {
            dirty.current = false;
            setDialog(null);
            onTree();
            navigate(page.parent ? `/knowledge/${page.parent}` : '/knowledge');
          }}
        />
      ) : null}
    </article>
  );
}

function TitleInput({
  value,
  onChange,
  onEnter,
  onDown,
  inputRef,
}: {
  value: string;
  onChange: (v: string) => void;
  onEnter: () => void;
  onDown: () => void;
  inputRef: React.RefObject<HTMLTextAreaElement | null>;
}) {
  useEffect(() => {
    const a = inputRef.current;
    if (!a) return;
    a.style.height = '0px';
    a.style.height = `${a.scrollHeight}px`;
  }, [value, inputRef]);
  return (
    <textarea
      ref={inputRef}
      className="kb-title kb-title-input"
      rows={1}
      value={value}
      maxLength={200}
      placeholder={t('kb.untitled')}
      aria-label={t('kb.page_title')}
      onChange={(e) => onChange(e.target.value.replace(/\n/g, ' '))}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          onEnter();
        } else if (e.key === 'ArrowDown' && e.currentTarget.selectionStart === e.currentTarget.value.length) {
          e.preventDefault();
          onDown();
        }
      }}
    />
  );
}

function Popover({ children, onClose }: { children: ReactNode; onClose: () => void }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    ref.current?.querySelector<HTMLElement>('button')?.focus();
    const away = (e: MouseEvent) => !ref.current?.parentElement?.contains(e.target as Node) && onClose();
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('mousedown', away);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', away);
      document.removeEventListener('keydown', esc);
    };
  }, [onClose]);
  return (
    <div className="kb-pop" role="menu" ref={ref}>
      {children}
    </div>
  );
}
function MenuItem({ icon, label, hint, danger, onClick }: { icon: IconName; label: string; hint?: string; danger?: boolean; onClick: () => void }) {
  return (
    <button type="button" role="menuitem" className={danger ? 'danger' : ''} onClick={onClick}>
      <Icon name={icon} size={16} />
      <span>{label}</span>
      {hint ? <span className="kb-pop-hint">{hint}</span> : null}
    </button>
  );
}

function EmojiDialog({ value, onClose, onPick }: { value: string | null; onClose: () => void; onPick: (e: string | null) => void }) {
  const [own, setOwn] = useState('');
  return (
    <Dialog
      title={t('kb.icon_title')}
      onClose={onClose}
      size="sm"
      footer={
        <>
          {value ? (
            <Button variant="ghost" icon="trash" onClick={() => onPick(null)}>
              {t('kb.remove_icon')}
            </Button>
          ) : null}
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
        </>
      }
    >
      <div className="kb-emoji-grid" role="listbox" aria-label={t('kb.icon_title')}>
        {EMOJIS.map((e) => (
          <button key={e} type="button" role="option" aria-selected={value === e} className={value === e ? 'on' : ''} onClick={() => onPick(e)}>
            {e}
          </button>
        ))}
      </div>
      <label className="field kb-emoji-own">
        <span className="field-label">{t('kb.icon_own')}</span>
        <span className="kb-emoji-own-row">
          <input className="input" value={own} maxLength={8} onChange={(e) => setOwn(e.target.value)} placeholder="🙂" />
          <Button disabled={!own.trim()} onClick={() => onPick(own.trim())}>
            {t('kb.use')}
          </Button>
        </span>
      </label>
    </Dialog>
  );
}

function TrashedBanner({ page, home, onDone }: { page: Page; home: Home; onDone: () => void }) {
  const navigate = useNavigate();
  const { fail, toast } = useUi();
  const [busy, setBusy] = useState(false);
  const [purging, setPurging] = useState(false);
  const canPurge = page.can_share || home.manager;
  return (
    <div className="kb-banner danger" role="status">
      <Icon name="trash" />
      <p>{page.trashed_by ? t('kb.in_trash_by', { name: page.trashed_by, when: ago(page.trashed_at!) }) : t('kb.in_trash')}</p>
      <Button
        size="sm"
        variant="primary"
        busy={busy}
        onClick={async () => {
          setBusy(true);
          try {
            await action('knowledge.restore', { id: page.id });
            toast(t('kb.restored'));
            onDone();
          } catch (e) {
            fail(e);
          } finally {
            setBusy(false);
          }
        }}
      >
        {t('kb.restore')}
      </Button>
      {canPurge ? (
        <Button size="sm" variant="danger" onClick={() => setPurging(true)}>
          {t('kb.purge')}
        </Button>
      ) : null}
      {purging ? (
        <Confirm
          title={t('kb.purge_title')}
          body={t('kb.purge_body', { title: untitled(page.title) })}
          confirm={t('kb.purge')}
          danger
          onClose={() => setPurging(false)}
          onConfirm={async () => {
            try {
              await action('knowledge.purge', { id: page.id });
              onDone();
              navigate('/knowledge?view=trash');
            } catch (e) {
              fail(e);
            }
          }}
        />
      ) : null}
    </div>
  );
}

function TrashConfirm({ page, home, onClose, onDone }: { page: Page; home: Home; onClose: () => void; onDone: () => void }) {
  const { fail, toast } = useUi();
  const [busy, setBusy] = useState(false);
  const count = useMemo(() => {
    const kids = new Map<string, string[]>();
    for (const p of home.pages) if (p.parent) kids.set(p.parent, [...(kids.get(p.parent) ?? []), p.id]);
    let n = 0;
    const walk = (id: string) => (kids.get(id) ?? []).forEach((k) => (n++, walk(k)));
    walk(page.id);
    return n;
  }, [home.pages, page.id]);
  return (
    <Confirm
      title={t('kb.trash_title')}
      body={
        <>
          <p>{t('kb.trash_body', { title: untitled(page.title) })}</p>
          {count ? <p>{tn('kb.trash_with', count)}</p> : null}
          <p className="muted small">{t('kb.trash_days')}</p>
        </>
      }
      confirm={t('kb.to_trash')}
      danger
      busy={busy}
      onClose={onClose}
      onConfirm={async () => {
        setBusy(true);
        try {
          await action('knowledge.trash', { id: page.id });
          toast(t('kb.trashed'));
          onDone();
        } catch (e) {
          fail(e);
        } finally {
          setBusy(false);
        }
      }}
    />
  );
}

// ---- sharing ------------------------------------------------------------------------------------

function ShareDialog({ page, home, onClose, onSaved }: { page: Page; home: Home; onClose: () => void; onSaved: () => void }) {
  const { fail, toast } = useUi();
  const r = page.root;
  const [vis, setVis] = useState(r.visibility);
  const [can, setCan] = useState<Access>(r.everyone_can);
  const [people, setPeople] = useState(r.members.map((m) => ({ user: m.user, access: m.access })));
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(false);
  const mineOwner = r.owner === home.me;
  const nameOf = new Map(home.people.map((p) => [p.id, p.name]));
  const candidates = home.people.filter((p) => p.id !== r.owner && !people.some((m) => m.user === p.id) && (!q || p.name.toLowerCase().includes(q.toLowerCase()))).slice(0, 6);
  const options: { key: typeof vis; can?: Access; icon: IconName; label: MessageKey; hint: MessageKey; disabled?: boolean }[] = [
    { key: 'workspace', can: 'write', icon: 'layers', label: 'kb.share_ws_write', hint: 'kb.share_ws_write_hint' },
    { key: 'workspace', can: 'read', icon: 'eye', label: 'kb.share_ws_read', hint: 'kb.share_ws_read_hint', disabled: !mineOwner && !home.manager },
    { key: 'members', icon: 'users', label: 'kb.share_members', hint: 'kb.share_members_hint' },
    { key: 'private', icon: 'lock', label: 'kb.share_private', hint: 'kb.share_private_hint', disabled: !mineOwner },
  ];
  const chosen = (o: (typeof options)[number]) => vis === o.key && (o.key !== 'workspace' || can === o.can);
  return (
    <Dialog
      title={t('kb.share_title')}
      onClose={onClose}
      size="md"
      footer={
        page.can_share ? (
          <>
            <Button variant="ghost" onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="primary"
              busy={busy}
              onClick={async () => {
                setBusy(true);
                try {
                  const list = vis === 'members' && !mineOwner && !people.some((m) => m.user === home.me) ? [...people, { user: home.me, access: 'write' as Access }] : people;
                  await action('knowledge.share', { id: page.id, visibility: vis, everyone_can: can, members: vis === 'members' ? list : [] });
                  toast(t('kb.share_saved'));
                  onSaved();
                } catch (e) {
                  fail(e);
                } finally {
                  setBusy(false);
                }
              }}
            >
              {t('common.save')}
            </Button>
          </>
        ) : (
          <Button onClick={onClose}>{t('common.close')}</Button>
        )
      }
    >
      <div className="kb-share">
        {page.path.length ? (
          <p className="kb-banner info small">
            <Icon name="info" size={16} />
            <span>{t('kb.share_on_root', { title: `${r.icon ? `${r.icon} ` : ''}${untitled(r.title)}` })}</span>
          </p>
        ) : null}
        <div className="kb-share-opts" role="radiogroup" aria-label={t('kb.share_who')}>
          {options.map((o) => (
            <label key={`${o.key}-${o.can ?? ''}`} className={`kb-share-opt${chosen(o) ? ' on' : ''}${o.disabled || !page.can_share ? ' off' : ''}`}>
              <input
                type="radio"
                name="kb-vis"
                checked={chosen(o)}
                disabled={o.disabled || !page.can_share}
                onChange={() => {
                  setVis(o.key);
                  if (o.can) setCan(o.can);
                }}
              />
              <Icon name={o.icon} />
              <span>
                <strong>{t(o.label)}</strong>
                <span className="muted small">{t(o.hint)}</span>
              </span>
            </label>
          ))}
        </div>
        {vis === 'members' ? (
          <div className="kb-people">
            <h3 className="kb-people-head">{t('kb.share_people')}</h3>
            <ul>
              <li>
                <span className="kb-person">{r.owner_name || nameOf.get(r.owner)}</span>
                <span className="muted small">{t('kb.share_owner')}</span>
              </li>
              {people.map((m) => (
                <li key={m.user}>
                  <span className="kb-person">{nameOf.get(m.user) ?? '—'}</span>
                  <select
                    className="input input-sm"
                    value={m.access}
                    disabled={!page.can_share}
                    aria-label={t('kb.share_access_for', { name: nameOf.get(m.user) ?? '' })}
                    onChange={(e) => setPeople(people.map((x) => (x.user === m.user ? { ...x, access: e.target.value as Access } : x)))}
                  >
                    <option value="write">{t('kb.can_edit')}</option>
                    <option value="read">{t('kb.can_read')}</option>
                  </select>
                  {page.can_share ? <IconButton icon="x" label={t('kb.share_remove', { name: nameOf.get(m.user) ?? '' })} onClick={() => setPeople(people.filter((x) => x.user !== m.user))} /> : null}
                </li>
              ))}
            </ul>
            {page.can_share ? (
              <div className="kb-add-person">
                <input className="input" type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('kb.share_add_ph')} aria-label={t('kb.share_add_ph')} />
                {q || people.length < 3 ? (
                  <div className="kb-person-pick">
                    {candidates.map((p) => (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => {
                          setPeople([...people, { user: p.id, access: 'write' }]);
                          setQ('');
                        }}
                      >
                        <Icon name="plus" size={14} />
                        {p.name}
                      </button>
                    ))}
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </Dialog>
  );
}

// ---- moving -------------------------------------------------------------------------------------

function MoveDialog({ page, home, onClose, onMoved }: { page: Page; home: Home; onClose: () => void; onMoved: () => void }) {
  const { fail, toast } = useUi();
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(false);
  const blocked = useMemo(() => {
    const kids = new Map<string, string[]>();
    for (const p of home.pages) if (p.parent) kids.set(p.parent, [...(kids.get(p.parent) ?? []), p.id]);
    const out = new Set([page.id]);
    const walk = (id: string) => (kids.get(id) ?? []).forEach((k) => (out.add(k), walk(k)));
    walk(page.id);
    return out;
  }, [home.pages, page.id]);
  const targets = home.pages.filter((p) => p.can_write && !blocked.has(p.id) && p.id !== page.parent && (!q || untitled(p.title).toLowerCase().includes(q.toLowerCase()))).slice(0, 40);
  const go = async (input: Record<string, unknown>) => {
    setBusy(true);
    try {
      await action('knowledge.move', { id: page.id, ...input });
      toast(t('kb.moved'));
      onMoved();
    } catch (e) {
      fail(e);
    } finally {
      setBusy(false);
    }
  };
  const pathOf = (p: TreePage) => {
    const byId = new Map(home.pages.map((x) => [x.id, x]));
    const names: string[] = [];
    for (let x = byId.get(p.parent ?? ''); x; x = byId.get(x.parent ?? '')) names.unshift(untitled(x.title));
    return [t(`kb.sec_${p.section}` as MessageKey), ...names].join(' › ');
  };
  return (
    <Dialog title={t('kb.move_title', { title: untitled(page.title) })} onClose={onClose} size="md">
      <div className="kb-move">
        <input className="input" type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('kb.move_find')} aria-label={t('kb.move_find')} data-autofocus />
        <div className="kb-move-list">
          {!q ? (
            <>
              {page.parent || page.section !== 'workspace' ? (
                <button type="button" disabled={busy} onClick={() => void go({ parent: null, section: 'workspace' })}>
                  <Icon name="layers" size={16} />
                  <span>{t('kb.move_top_ws')}</span>
                </button>
              ) : null}
              {page.parent || page.section !== 'private' ? (
                <button type="button" disabled={busy} onClick={() => void go({ parent: null, section: 'private' })}>
                  <Icon name="lock" size={16} />
                  <span>{t('kb.move_top_private')}</span>
                </button>
              ) : null}
            </>
          ) : null}
          {targets.map((p) => (
            <button key={p.id} type="button" disabled={busy} onClick={() => void go({ parent: p.id })}>
              <span className="kb-emoji" aria-hidden="true">
                {p.icon || <Icon name="text" size={16} />}
              </span>
              <span>
                {untitled(p.title)}
                <span className="muted small kb-move-path">{pathOf(p)}</span>
              </span>
            </button>
          ))}
          {q && !targets.length ? <p className="muted small">{t('kb.no_pages_found')}</p> : null}
        </div>
      </div>
    </Dialog>
  );
}

// ---- versions -----------------------------------------------------------------------------------

function HistoryDialog({ page, onClose, onRestored }: { page: Page; onClose: () => void; onRestored: () => void }) {
  const { fail } = useUi();
  const [list, setList] = useState<{ id: string; at: string; author: string | null; version: number; title: string; blocks: number }[] | null>(null);
  const [sel, setSel] = useState<string | null>(null);
  const [rev, setRev] = useState<{ id: string; title: string; icon: string | null; body: Block[] } | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    action<typeof list>('knowledge.history', { id: page.id }).then((l) => {
      setList(l);
      setSel(l?.[1]?.id ?? l?.[0]?.id ?? null);
    }, fail);
  }, [page.id, fail]);
  useEffect(() => {
    if (!sel) return;
    setRev(null);
    action<NonNullable<typeof rev>>('knowledge.revision', { id: sel }).then(setRev, fail);
  }, [sel, fail]);
  const isCurrent = list?.[0]?.id === sel;
  return (
    <Dialog
      title={t('kb.history_title')}
      onClose={onClose}
      size="xl"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.close')}
          </Button>
          {page.can_write ? (
            <Button
              variant="primary"
              icon="undo"
              disabled={!sel || isCurrent}
              busy={busy}
              onClick={async () => {
                setBusy(true);
                try {
                  await action('knowledge.restore_revision', { id: sel });
                  onRestored();
                } catch (e) {
                  fail(e);
                } finally {
                  setBusy(false);
                }
              }}
            >
              {t('kb.restore_version')}
            </Button>
          ) : null}
        </>
      }
    >
      <div className="kb-history">
        <ul className="kb-versions" aria-label={t('kb.versions')}>
          {!list ? (
            <Skeleton h={200} />
          ) : (
            list.map((v, i) => (
              <li key={v.id}>
                <button type="button" className={sel === v.id ? 'on' : ''} aria-pressed={sel === v.id} onClick={() => setSel(v.id)}>
                  <strong>{fullDate(v.at)}</strong>
                  <span className="muted small">
                    {v.author ?? '—'}
                    {i === 0 ? ` · ${t('kb.current_version')}` : ''}
                  </span>
                </button>
              </li>
            ))
          )}
        </ul>
        <div className="kb-preview">
          {rev ? (
            <>
              <h2 className="kb-title">
                {rev.icon ? <span aria-hidden="true">{rev.icon} </span> : null}
                {untitled(rev.title)}
              </h2>
              <Editor pageId={page.id} blocks={rev.body} onChange={() => undefined} readOnly pages={[]} onSubpage={async () => null} onTitleFocus={() => undefined} />
            </>
          ) : (
            <Skeleton h={240} />
          )}
        </div>
      </div>
    </Dialog>
  );
}

// ---- search -------------------------------------------------------------------------------------

function SearchDialog({ home, onClose }: { home: Home; onClose: () => void }) {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [hits, setHits] = useState<{ id: string; title: string; icon: string | null; parent: string | null; snippet: string }[] | null>(null);
  const [sel, setSel] = useState(0);
  useEffect(() => {
    const query = q.trim();
    if (!query) return setHits(null);
    let live = true;
    const timer = setTimeout(() => {
      action<NonNullable<typeof hits>>('knowledge.search', { q: query }).then(
        (h) => {
          if (live) {
            setHits(h);
            setSel(0);
          }
        },
        () => live && setHits([]),
      );
    }, 180);
    return () => {
      live = false;
      clearTimeout(timer);
    };
  }, [q]);
  const byId = new Map(home.pages.map((p) => [p.id, p]));
  const where = (parent: string | null, id: string) => {
    const names: string[] = [];
    for (let x = byId.get(parent ?? ''); x; x = byId.get(x.parent ?? '')) names.unshift(untitled(x.title));
    const sec = byId.get(id)?.section;
    return [sec ? t(`kb.sec_${sec}` as MessageKey) : '', ...names].filter(Boolean).join(' › ');
  };
  const recent = home.recent
    .map((r) => byId.get(r.id))
    .filter((x): x is TreePage => !!x)
    .slice(0, 6);
  const list = hits ?? recent.map((p) => ({ id: p.id, title: p.title, icon: p.icon, parent: p.parent, snippet: '' }));
  const go = (id: string) => {
    onClose();
    navigate(`/knowledge/${id}`);
  };
  const mark = (text: string) => {
    const query = q.trim();
    if (!query) return text;
    const i = text.toLowerCase().indexOf(query.toLowerCase());
    if (i < 0) return text;
    return (
      <>
        {text.slice(0, i)}
        <mark>{text.slice(i, i + query.length)}</mark>
        {text.slice(i + query.length)}
      </>
    );
  };
  return (
    <Dialog title={t('kb.search_title')} onClose={onClose} size="md">
      <div className="kb-searchbox">
        <Icon name="search" />
        <input
          className="input"
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t('kb.search_ph')}
          aria-label={t('kb.search_ph')}
          data-autofocus
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown') {
              e.preventDefault();
              setSel((s) => Math.min(list.length - 1, s + 1));
            } else if (e.key === 'ArrowUp') {
              e.preventDefault();
              setSel((s) => Math.max(0, s - 1));
            } else if (e.key === 'Enter' && list[sel]) go(list[sel]!.id);
          }}
        />
      </div>
      <p className="muted small kb-search-label">{hits ? tn('kb.n_results', hits.length) : recent.length ? t('kb.recent') : ''}</p>
      <ul className="kb-results" role="listbox" aria-label={t('kb.search_title')}>
        {list.map((h, i) => (
          <li key={h.id}>
            <button type="button" role="option" aria-selected={i === sel} className={i === sel ? 'on' : ''} onMouseEnter={() => setSel(i)} onClick={() => go(h.id)}>
              <span className="kb-emoji" aria-hidden="true">
                {h.icon || <Icon name="text" size={16} />}
              </span>
              <span className="kb-result-main">
                <strong>{mark(untitled(h.title))}</strong>
                <span className="muted small">{where(h.parent, h.id)}</span>
                {h.snippet ? <span className="kb-snippet small">{mark(h.snippet)}</span> : null}
              </span>
            </button>
          </li>
        ))}
        {hits && !hits.length ? <li className="muted kb-none">{t('kb.no_results', { q: q.trim() })}</li> : null}
      </ul>
    </Dialog>
  );
}

// ---- the start page ------------------------------------------------------------------------------

function HomeView({
  home,
  onCreate,
  onSearch,
  onMenu,
}: {
  home: Home;
  onCreate: (parent: string | null, section?: 'workspace' | 'private', tpl?: string) => Promise<string | null>;
  onSearch: () => void;
  onMenu?: () => void;
}) {
  const byId = new Map(home.pages.map((p) => [p.id, p]));
  const recent = home.recent.map((r) => ({ p: byId.get(r.id), at: r.at })).filter((x): x is { p: TreePage; at: string } => !!x.p);
  const latest = [...home.pages].sort((a, b) => b.edited_at.localeCompare(a.edited_at)).slice(0, 6);
  const favs = home.favorites.map((f) => byId.get(f)).filter((x): x is TreePage => !!x);
  const card = (p: TreePage, sub: string) => (
    <Link key={p.id} to={`/knowledge/${p.id}`} className="kb-tile">
      <span className="kb-tile-emoji" aria-hidden="true">
        {p.icon || <Icon name="text" size={22} />}
      </span>
      <strong>{untitled(p.title)}</strong>
      <span className="muted small">{sub}</span>
    </Link>
  );
  return (
    <div className="kb-home">
      <header className="kb-home-head">
        {onMenu ? <IconButton icon="menu" label={t('kb.pages')} onClick={onMenu} /> : null}
        <div>
          <h1>{t('kb.title')}</h1>
          <p className="muted">{t('kb.subtitle')}</p>
        </div>
      </header>
      <button type="button" className="kb-home-search" onClick={onSearch}>
        <Icon name="search" />
        <span>{t('kb.search_ph')}</span>
      </button>
      {!home.pages.length ? (
        <EmptyState
          icon="notebook"
          title={t('kb.empty')}
          body={t('kb.empty_hint')}
          action={
            <Button variant="primary" icon="plus" onClick={() => void onCreate(null, 'workspace')}>
              {t('kb.new_page')}
            </Button>
          }
        />
      ) : null}
      {favs.length ? (
        <section>
          <h2 className="kb-home-h">{t('kb.sec_favorites')}</h2>
          <div className="kb-tiles">{favs.map((p) => card(p, t(`kb.sec_${p.section}` as MessageKey)))}</div>
        </section>
      ) : null}
      {recent.length ? (
        <section>
          <h2 className="kb-home-h">{t('kb.recent')}</h2>
          <div className="kb-tiles">{recent.slice(0, 6).map(({ p, at }) => card(p, t('kb.opened', { when: ago(at) })))}</div>
        </section>
      ) : null}
      {latest.length ? (
        <section>
          <h2 className="kb-home-h">{t('kb.latest')}</h2>
          <div className="kb-tiles">{latest.map((p) => card(p, t('kb.edited', { when: ago(p.edited_at) })))}</div>
        </section>
      ) : null}
      <section>
        <h2 className="kb-home-h">{t('kb.start_from')}</h2>
        <div className="kb-tiles">
          <button type="button" className="kb-tile" onClick={() => void onCreate(null, 'workspace')}>
            <span className="kb-tile-emoji" aria-hidden="true">
              <Icon name="plus" size={22} />
            </span>
            <strong>{t('kb.tpl_blank')}</strong>
            <span className="muted small">{t('kb.tpl_blank_hint')}</span>
          </button>
          {TEMPLATES.map((tp) => (
            <button key={tp.key} type="button" className="kb-tile" onClick={() => void onCreate(null, 'workspace', tp.key)}>
              <span className="kb-tile-emoji" aria-hidden="true">
                {tp.icon}
              </span>
              <strong>{t(tp.label)}</strong>
              <span className="muted small">{t(tp.hint)}</span>
            </button>
          ))}
        </div>
      </section>
    </div>
  );
}

// ---- the trash -----------------------------------------------------------------------------------

function TrashView({ home, onChanged, onMenu }: { home: Home; onChanged: () => void; onMenu?: () => void }) {
  const { fail, toast } = useUi();
  const navigate = useNavigate();
  const [list, setList] = useState<
    { id: string; title: string; icon: string | null; section: Section; trashed_at: string; trashed_by: string | null; with_pages: number; gone_at: string; can_write: boolean }[] | null
  >(null);
  const [purging, setPurging] = useState<{ id: string; title: string } | null>(null);
  const load = useCallback(() => action<NonNullable<typeof list>>('knowledge.trash_list').then(setList, fail), [fail]);
  useEffect(() => {
    void load();
  }, [load, home.trash]);
  const days = (iso: string) => Math.max(0, Math.ceil((Date.parse(iso) - Date.now()) / 86_400_000));
  return (
    <div className="kb-home">
      <header className="kb-home-head">
        {onMenu ? <IconButton icon="menu" label={t('kb.pages')} onClick={onMenu} /> : null}
        <div>
          <h1>{t('kb.trash')}</h1>
          <p className="muted">{t('kb.trash_hint')}</p>
        </div>
      </header>
      {!list ? (
        <Skeleton h={200} />
      ) : !list.length ? (
        <EmptyState icon="trash" title={t('kb.trash_empty')} />
      ) : (
        <ul className="kb-trash">
          {list.map((p) => (
            <li key={p.id}>
              <button type="button" className="kb-trash-main" onClick={() => navigate(`/knowledge/${p.id}`)}>
                <span className="kb-emoji" aria-hidden="true">
                  {p.icon || <Icon name="text" size={16} />}
                </span>
                <span>
                  <strong>{untitled(p.title)}</strong>
                  <span className="muted small">
                    {[
                      t(`kb.sec_${p.section}` as MessageKey),
                      p.trashed_by ? t('kb.trashed_by', { name: p.trashed_by, when: ago(p.trashed_at) }) : ago(p.trashed_at),
                      p.with_pages ? tn('kb.with_pages', p.with_pages) : '',
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </span>
                </span>
              </button>
              <span className="kb-gone small">{tn('kb.gone_in', days(p.gone_at))}</span>
              {p.can_write ? (
                <span className="kb-trash-acts">
                  <Button
                    size="sm"
                    icon="undo"
                    onClick={async () => {
                      try {
                        await action('knowledge.restore', { id: p.id });
                        toast(t('kb.restored'));
                        onChanged();
                        void load();
                      } catch (e) {
                        fail(e);
                      }
                    }}
                  >
                    {t('kb.restore')}
                  </Button>
                  <IconButton icon="trash" label={t('kb.purge_named', { title: untitled(p.title) })} onClick={() => setPurging({ id: p.id, title: p.title })} />
                </span>
              ) : null}
            </li>
          ))}
        </ul>
      )}
      {purging ? (
        <Confirm
          title={t('kb.purge_title')}
          body={t('kb.purge_body', { title: untitled(purging.title) })}
          confirm={t('kb.purge')}
          danger
          onClose={() => setPurging(null)}
          onConfirm={async () => {
            try {
              await action('knowledge.purge', { id: purging.id });
              setPurging(null);
              onChanged();
              void load();
            } catch (e) {
              fail(e);
            }
          }}
        />
      ) : null}
    </div>
  );
}

export { Inline };
